import * as React from "react";
import { GripVertical } from "lucide-react";
import * as ResizablePrimitive from "react-resizable-panels";

import { FOCUS_RING_GROUP } from "../lib/focus";
import { cn } from "../lib/utils";
import { Icon } from "./icon";

/**
 * v2 Resizable (redesign phase 5, #455). Storybook only until implementation;
 * see docs/design-library/04-implementation-notes.md for the swap. Same names
 * as v1 (`ResizablePanelGroup`, `ResizablePanel`, `ResizableHandle` with
 * `withHandle`), so the swap is a move.
 *
 * Handle (break #129): a 1px `--divider` line, the same hairline that
 * separates panes elsewhere. The pointer hit area is the library's
 * (`hitAreaMargins`: 5px each side for a mouse, 15 for touch), so the line
 * can stay thin. On hover and while dragging the line turns `--input` and a
 * grip appears (GripVertical 12 in a 16×24 `--card` box with an `--input`
 * border); with keyboard focus the grip gets the 2px `--ring` outline.
 * `withHandle` keeps the grip visible at rest, for panes whose edge is
 * otherwise hard to find.
 *
 * Keyboard (the library's window-splitter pattern): the handle is a focusable
 * `separator` with `aria-valuenow`; arrows resize by 10%, Shift+arrow and
 * Home / End go to the limits. It needs a name: "Größe ändern" by default;
 * give each site a specific one ("Breite der Konversationsliste ändern").
 *
 * The library's own `data-resize-handle-state` (hover / drag) and
 * `data-panel-group-direction` attributes drive the styles, like Radix state
 * variants. `data-hover` / `data-focus` pin the states in stories.
 */

const ResizablePanelGroup = ({ className, ...props }: React.ComponentProps<typeof ResizablePrimitive.PanelGroup>) => (
  <ResizablePrimitive.PanelGroup
    className={cn("flex h-full w-full data-[panel-group-direction=vertical]:flex-col", className)}
    {...props}
  />
);

const ResizablePanel = ResizablePrimitive.Panel;

const GRIP_SHOWN =
  "group-data-[resize-handle-state=hover]:opacity-100 group-data-[resize-handle-state=drag]:opacity-100 " +
  "group-focus-visible:opacity-100 group-data-hover:opacity-100 group-data-focus:opacity-100";

const GRIP_RING = FOCUS_RING_GROUP;

export type ResizableHandleProps = React.ComponentProps<typeof ResizablePrimitive.PanelResizeHandle> & {
  /** Keep the grip visible at rest. */
  withHandle?: boolean;
  /** Static states for stories; apps never set them. */
  "data-hover"?: string;
  "data-focus"?: string;
};

const ResizableHandle = ({
  withHandle = false,
  "aria-label": ariaLabel = "Größe ändern",
  className,
  ...props
}: ResizableHandleProps) => (
  <ResizablePrimitive.PanelResizeHandle
    aria-label={ariaLabel}
    className={cn(
      "group relative flex w-px shrink-0 items-center justify-center bg-divider outline-hidden transition-colors",
      "data-[resize-handle-state=hover]:bg-input data-[resize-handle-state=drag]:bg-input data-hover:bg-input",
      "data-[panel-group-direction=vertical]:h-px data-[panel-group-direction=vertical]:w-full",
      className,
    )}
    {...props}
  >
    <span
      aria-hidden="true"
      className={cn(
        "z-10 flex h-6 w-4 shrink-0 items-center justify-center rounded-sm border border-input bg-card text-ink-muted transition-opacity",
        "group-data-[panel-group-direction=vertical]:rotate-90",
        withHandle ? "opacity-100" : "opacity-0",
        GRIP_SHOWN,
        GRIP_RING,
      )}
    >
      <Icon icon={GripVertical} size={12} />
    </span>
  </ResizablePrimitive.PanelResizeHandle>
);

export { ResizablePanelGroup, ResizablePanel, ResizableHandle };
